import type { GenerationSlug, Member, SortMode, VideoKind } from '../types.ts';
import { GENERATIONS, GENERATION_BY_ID } from '../data/styles.ts';

interface Props {
  members: Member[];

  groupFilter: GenerationSlug | 'all';
  onGroupChange: (g: GenerationSlug | 'all') => void;

  memberFilter: Set<string>;
  onMemberToggle: (slug: string) => void;
  onMemberClear: () => void;

  kindFilter: VideoKind | 'all';
  onKindChange: (k: VideoKind | 'all') => void;

  sort: SortMode;
  onSortToggle: () => void;
}

export function FilterBar({
  members,
  groupFilter,
  onGroupChange,
  memberFilter,
  onMemberToggle,
  onMemberClear,
  kindFilter,
  onKindChange,
  sort,
  onSortToggle,
}: Props) {
  const visibleMembers = groupFilter === 'all'
    ? members
    : members.filter((m) => m.group === groupFilter);

  const groupTabs = [
    { id: 'all' as const, ko: '전체' },
    ...GENERATIONS.map((g) => ({ id: g.id, ko: g.ko, accent: g.accent, tint: g.tint })),
  ];

  return (
    <section className="ss-filters">
      <div className="ss-filters__inner">
        <nav className="ss-tabs" role="tablist">
          {groupTabs.map((t) => {
            const active = groupFilter === t.id;
            const gen = t.id === 'all' ? null : GENERATION_BY_ID[t.id as GenerationSlug];
            return (
              <button
                key={t.id}
                className={`ss-tab ${active ? 'ss-tab--on' : ''}`}
                style={
                  active && gen
                    ? { ['--tab-c' as string]: gen.accent, ['--tab-soft' as string]: gen.tint }
                    : undefined
                }
                onClick={() => onGroupChange(t.id)}
                role="tab"
                aria-selected={active}
              >
                <span className="ss-tab__label">{t.ko}</span>
              </button>
            );
          })}
        </nav>

        <div className="ss-members" role="group" aria-label="멤버 필터">
          <button
            className={`ss-mchip ${memberFilter.size === 0 ? 'ss-mchip--all-on' : ''}`}
            onClick={onMemberClear}
          >
            전체 멤버
          </button>
          {visibleMembers.map((m) => {
            const active = memberFilter.has(m.id);
            return (
              <button
                key={m.id}
                className={`ss-mchip ${active ? 'ss-mchip--on' : ''}`}
                style={{
                  ['--mc' as string]: m.color,
                  ['--mc-soft' as string]: m.soft,
                }}
                onClick={() => onMemberToggle(m.id)}
                aria-pressed={active}
              >
                <span>{m.nameKr}</span>
              </button>
            );
          })}
        </div>

        <div className="ss-controls">
          <div className="ss-seg" role="tablist" aria-label="채널 종류">
            {([
              { id: 'all',    ko: '전체' },
              { id: 'edited', ko: '편집' },
              { id: 'replay', ko: '다시보기' },
              { id: 'shorts', ko: '쇼츠' },
            ] as const).map((o) => (
              <button
                key={o.id}
                className={`ss-seg__btn ${kindFilter === o.id ? 'ss-seg__btn--on' : ''}`}
                onClick={() => onKindChange(o.id)}
              >
                {o.ko}
              </button>
            ))}
          </div>

          <button
            className="ss-sort"
            onClick={onSortToggle}
            title="정렬 방향 전환"
          >
            <span>{sort === 'newest' ? '최신순' : sort === 'oldest' ? '오래된순' : '관련도순'}</span>
            <span className="ss-sort__ico" aria-hidden="true">
              {sort === 'newest' ? '↓' : sort === 'oldest' ? '↑' : '★'}
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}
